import "./SingleAdminFundraiserDisplay.css";

import { useEffect, useState, useRef } from "react";
import ProfitCircle from "../CoordAndAdmin/ProfitCircle";
import ActiveParticipants from "../CoordAndAdmin/ActiveParticipants";
import Last7DaySales from "../CoordAndAdmin/SevenDaySales";
import TeamAndSales from "../CoordAndAdmin/TeamAndSales";
import ParticipantSalesDataTable from "../CoordAndAdmin/ParticipantDataSalesTable";
import {
  ProfitDetailsResponse,
  ActiveParticipantsResponse,
  SevenDaySalesResponse,
  TeamAndSalesResponse,
  ParticipantSalesDataTableListResponse,
} from "../../../Interfaces/TeamAdminCoordDashboard";
import config from "../../../config";
import { TeamAdminDashboardData } from "../../../Interfaces/TeamAdminCoordDashboard";
import apiClient from "../../../services/apiClient";

import Popup from "../../Forms/StartingYourFundraiser/Step4-AssignParticipants/Popup";
import { ManualUploadPopupBody } from "../../Forms/StartingYourFundraiser/Step4-AssignParticipants/PopupHandlers";
import SummaryButtonDownload from "../../../pages/Dashboards/SummaryButtonDownload";

const BASE_API = config.API_BASE_URL;

type SingleFundraiserComponentProps = {
  accountID: number;
  division: string;
  primary_division: string;
  fundraiser_id: number;
  primary_div_id: number;
  secondary_div_id: number;
};

const SingleAdminFundraiserComponentDisplay: React.FC<
  SingleFundraiserComponentProps
> = ({
  accountID,
  division,
  primary_division,
  fundraiser_id,
  primary_div_id,
  secondary_div_id,
}) => {
  const [profitInfo, setProfitInfo] = useState<ProfitDetailsResponse | null>(
    null
  );
  const [activeParticipants, setActiveParticipants] =
    useState<ActiveParticipantsResponse | null>(null);
  const [last7DaySales, setLast7DaySales] =
    useState<SevenDaySalesResponse | null>(null);
  const [teamAndSaleInfo, setTeamAndSaleInfo] =
    useState<TeamAndSalesResponse | null>(null);
  const [participantTableInfo, setParticipantTableInfo] =
    useState<ParticipantSalesDataTableListResponse | null>(null);
  const [dashboardData, setDashboardData] =
    useState<TeamAdminDashboardData | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [mounted, setMounted] = useState(false);
  const [dimsSet, setDimsSet] = useState(false);
  const [oneColumn, setOneColumn] = useState(false);

  type PopupContent = React.ReactNode;
  type PopupHeader = string;
  const [isPopupOpen, setPopupOpen] = useState<boolean>(false);
  const [popupContent, setPopupContent] = useState<PopupContent>(null);
  const [popupHeader, setPopupHeader] = useState<PopupHeader>(
    "This shouldn't be seen"
  );

  const updateDimensions = () => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;

      const isSingleColumn = window.matchMedia("(max-width: 768px)").matches;
      setOneColumn(isSingleColumn);

      // Account for the number of columns
      const columns = isSingleColumn ? 1 : 2;
      const mult_pad = isSingleColumn ? 2 : 1;

      const padding = containerWidth * 0.1; // 10% padding
      const gap = 40; // Use the column-gap defined in your CSS --> 80 but div by 2
      const itemWidth = containerWidth / columns;
      const itemWithGap = itemWidth - gap;
      const absoluteMax = Math.min(itemWithGap, 400); // max pixel per element is 400
      const actualChildSize = absoluteMax - padding * mult_pad;
      const childWidth = Math.max(actualChildSize, 100); // set min pixel to 100

      // Calculate height using the aspect ratio of 2:2.7
      const aspectRatio = 2 / 2.7;
      const childHeight = childWidth / aspectRatio;

      setDimensions({ width: childWidth, height: childHeight });
    }
  };

  useEffect(() => {
    // Trigger necessary updates when props change
    // Fetch or update content here based on division or fundraiser_id
    fetchParticipantFundraisers();
  }, [division, fundraiser_id]);

  useEffect(() => {
    if (dashboardData) {
      const profit_info = {
        profit: dashboardData.total_profit,
        total_sales: dashboardData.total_sales_amount,
      };
      const active_info = dashboardData.participant_active_summary;
      const seven_days = dashboardData.last_seven_daily_sales;
      const team_sale = dashboardData.team_participant_sales_data;
      const part_table = {
        total_sales_data: dashboardData.participant_sales_table,
      };
      setProfitInfo(profit_info);
      setActiveParticipants(active_info);
      setLast7DaySales(seven_days);
      setTeamAndSaleInfo(team_sale);
      setParticipantTableInfo(part_table);
    }
  }, [dashboardData]);

  useEffect(() => {
    // Initial update on component mount
    // updateDimensions();
    // add a delay because sometimes it can check before the container is mounted and then it bugs out
    // const timeout = setTimeout(() => {
    //   updateDimensions(); // update dimensions after delay
    //   }, 100); // Delay of 100ms

    const updateIfContainerMounted = () => {
      if (containerRef.current) {
        updateDimensions();
      }
    };

    const observer = new MutationObserver(() => {
      if (containerRef.current) {
        updateIfContainerMounted();
        observer.disconnect(); // Stop observing once the container is found
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    // Add a resize listener to update the radius when the window is resized
    window.addEventListener("resize", updateDimensions);

    // Cleanup the event listener on component unmount
    // Cleanup the observer on unmount
    return () => {
      window.removeEventListener("resize", updateDimensions);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (dimensions.width > 0 && dimensions.height > 0) {
      setMounted(true);
      setDimsSet(true);
    }
  }, [dimensions]);

  const fetchParticipantFundraisers = async () => {
    // setLoading(true);
    try {
      const response = await apiClient.get(`${BASE_API}dashboards/teamAdmin/`, {
        params: {
          team_admin_account_id: accountID,
          fundraiser_id: fundraiser_id,
          secondary_division_id: secondary_div_id,
        },
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error("Error fetching team admin fundraisers", error);
      // setError("Failed to fetch team admin fundraisers");
    } finally {
      // setLoading(false);
    }
  };

  const openPopup = (header: PopupHeader, content: PopupContent) => {
    setPopupHeader(header);
    setPopupContent(content);
    setPopupOpen(true);
  };

  const closePopup = () => {
    setPopupOpen(false);
    setPopupContent(null);
  };

  const handleOnClick = () => {
    const header: PopupHeader = "Manual Upload";
    const content: PopupContent = (
      <ManualUploadPopupBody
        onClose={closePopup}
        fundraiserID={fundraiser_id}
      />
    );

    openPopup(header, content);
  };

  if (
    !profitInfo ||
    !activeParticipants ||
    !last7DaySales ||
    !teamAndSaleInfo ||
    !participantTableInfo
  ) {
    // Render a loading state or placeholder if profitInfo is not ready
    return <div>Loading fundraiser details...</div>;
  }

  return (
    <div ref={containerRef}>
      {!(mounted && dimsSet) ? (
        <div>Loading fundraiser details...</div> // Render loading text if not mounted
      ) : (
        <div>
          <div className="team-admin-single-fundraiser-container">
            <div className="team-admin-square-element-wrapper">
              <ProfitCircle
                profit={profitInfo.profit}
                total_sales={profitInfo.total_sales}
                width={dimensions.width}
                height={dimensions.height}
              />
            </div>
            {/* <7 day sales /> */}
            <div className="team-admin-square-element-wrapper">
              <Last7DaySales
                days_of_week={last7DaySales.days_of_week}
                width={dimensions.width}
                height={dimensions.height}
              />
            </div>
            {/* <active participants /> */}
            <div className="team-admin-square-element-wrapper">
              <ActiveParticipants
                active={activeParticipants.active}
                yesterday={activeParticipants.yesterday}
                last_week={activeParticipants.last_week}
                inactive={activeParticipants.inactive}
                width={dimensions.width}
                height={dimensions.height}
              />
            </div>
            {/* <sellers and teams /> */}
            <div className="team-admin-square-element-wrapper">
              <TeamAndSales
                primary_div={teamAndSaleInfo.primary_div}
                secondary_div={teamAndSaleInfo.secondary_div}
                secondary_div_label={division}
                width={dimensions.width}
                height={dimensions.height}
              />
            </div>
            <SummaryButtonDownload
              total_sales_data={participantTableInfo.total_sales_data}
              one_col={oneColumn}
              width={dimensions.width}
              height={dimensions.height}
              file_name={`${division}-participant-summary`}
            />

            <button
              onClick={handleOnClick}
              className="team-admin-black-button-wrapper"
            >
              Add/email
              <br />
              participant
            </button>
            <Popup
              popupHeader={popupHeader}
              isOpen={isPopupOpen}
              onBackButton={closePopup}
              content={popupContent}
            />
          </div>
          <div className="team-admin-participant-data-table">
            <ParticipantSalesDataTable
              total_sales_data={participantTableInfo.total_sales_data}
              one_col={oneColumn}
              width={dimensions.width}
              height={dimensions.height}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SingleAdminFundraiserComponentDisplay;
